import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit, Trash2, DollarSign, Percent, Calculator, Eye, AlertCircle, CheckCircle, X, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPricing = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rule_type: 'markup_percent',
    markup_percent: 300,
    markup_fixed: 0,
    fixed_price: 0,
    quantity_tiers: [],
    scope: 'site_wide',
    category_ids: [],
    product_ids: [],
    priority: 0,
    status: 'active'
  });

  // Default tier template
  const defaultTier = { min_qty: 1, max_qty: null, markup_percent: 300, flat_addition: 0 };

  const fetchRules = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/rules`);
      const data = await response.json();
      setRules(data.rules || []);
    } catch (error) {
      toast.error('Failed to fetch pricing rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        markup_percent: formData.rule_type === 'markup_percent' ? parseFloat(formData.markup_percent) : null,
        markup_fixed: formData.rule_type === 'markup_fixed' ? parseFloat(formData.markup_fixed) : null,
        fixed_price: formData.rule_type === 'fixed_price' ? parseFloat(formData.fixed_price) : null,
        quantity_tiers: formData.rule_type === 'quantity_tier' ? formData.quantity_tiers.map(t => ({
          min_qty: parseInt(t.min_qty),
          max_qty: t.max_qty ? parseInt(t.max_qty) : null,
          markup_percent: parseFloat(t.markup_percent),
          flat_addition: parseFloat(t.flat_addition) || 0
        })) : null,
      };

      const url = editingRule 
        ? `${API_URL}/api/pricing/rules/${editingRule.id}`
        : `${API_URL}/api/pricing/rules`;
      
      const response = await fetch(url, {
        method: editingRule ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to save rule');

      toast.success(editingRule ? 'Rule updated!' : 'Rule created!');
      setShowModal(false);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this pricing rule?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/pricing/rules/${ruleId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete rule');
      
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      rule_type: rule.rule_type,
      markup_percent: rule.markup_percent || 300,
      markup_fixed: rule.markup_fixed || 0,
      fixed_price: rule.fixed_price || 0,
      quantity_tiers: rule.quantity_tiers || [],
      scope: rule.scope,
      category_ids: rule.category_ids || [],
      product_ids: rule.product_ids || [],
      priority: rule.priority,
      status: rule.status
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      rule_type: 'markup_percent',
      markup_percent: 300,
      markup_fixed: 0,
      fixed_price: 0,
      quantity_tiers: [],
      scope: 'site_wide',
      category_ids: [],
      product_ids: [],
      priority: 0,
      status: 'active'
    });
  };

  // Quantity tier management functions
  const addTier = () => {
    const lastTier = formData.quantity_tiers[formData.quantity_tiers.length - 1];
    const newMinQty = lastTier ? (lastTier.max_qty || lastTier.min_qty) + 1 : 1;
    setFormData({
      ...formData,
      quantity_tiers: [...formData.quantity_tiers, { ...defaultTier, min_qty: newMinQty }]
    });
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...formData.quantity_tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setFormData({ ...formData, quantity_tiers: newTiers });
  };

  const removeTier = (index) => {
    setFormData({
      ...formData,
      quantity_tiers: formData.quantity_tiers.filter((_, i) => i !== index)
    });
  };

  const handleClone = (rule) => {
    // Clone the rule but don't set editingRule (so it creates a new one)
    setEditingRule(null);
    setFormData({
      name: `Copy of ${rule.name}`,
      description: rule.description || '',
      rule_type: rule.rule_type,
      markup_percent: rule.markup_percent || 300,
      markup_fixed: rule.markup_fixed || 0,
      fixed_price: rule.fixed_price || 0,
      quantity_tiers: rule.quantity_tiers ? [...rule.quantity_tiers] : [],
      scope: rule.scope,
      category_ids: rule.category_ids ? [...rule.category_ids] : [],
      product_ids: rule.product_ids ? [...rule.product_ids] : [],
      priority: rule.priority + 1, // Increment priority so it takes precedence
      status: 'active'
    });
    setShowModal(true);
    toast.info('Rule cloned! Modify and save as new.');
  };

  const handlePreviewPricing = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/apply-rules?dry_run=true`, {
        method: 'POST'
      });
      const data = await response.json();
      setPreviewResult(data);
      toast.success(`Preview: ${data.products_to_update} products would be updated`);
    } catch (error) {
      toast.error('Failed to preview pricing');
    }
  };

  const handleApplyPricing = async () => {
    if (!window.confirm('Apply pricing rules to all products? This will update retail prices.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/pricing/apply-rules?dry_run=false`, {
        method: 'POST'
      });
      const data = await response.json();
      setApplyResult(data);
      toast.success(`Applied: ${data.products_to_update} products updated`);
    } catch (error) {
      toast.error('Failed to apply pricing');
    }
  };

  const getRuleTypeLabel = (type) => {
    const labels = {
      'markup_percent': 'Percentage Markup',
      'markup_fixed': 'Fixed Amount Markup',
      'fixed_price': 'Fixed Price Override',
      'quantity_tier': 'Quantity Tiers'
    };
    return labels[type] || type;
  };

  const getRuleDescription = (rule) => {
    switch (rule.rule_type) {
      case 'markup_percent':
        return `Cost × ${(1 + rule.markup_percent / 100).toFixed(2)} (${rule.markup_percent}% markup)`;
      case 'markup_fixed':
        return `Cost + $${rule.markup_fixed?.toFixed(2)}`;
      case 'fixed_price':
        return `Fixed at $${rule.fixed_price?.toFixed(2)}`;
      case 'quantity_tier':
        const tiers = rule.quantity_tiers || [];
        if (tiers.length === 0) return 'No tiers defined';
        return `${tiers.length} tier${tiers.length > 1 ? 's' : ''} (${tiers[0]?.min_qty || 1}-${tiers[tiers.length - 1]?.max_qty || '∞'} qty)`;
      default:
        return rule.rule_type;
    }
  };

  const getScopeLabel = (scope) => {
    const labels = {
      'site_wide': 'All Products',
      'category': 'Category',
      'subcategory': 'Subcategory',
      'product': 'Specific Products'
    };
    return labels[scope] || scope;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-semibold text-ivory-800">Pricing Engine</h2>
          <p className="text-ivory-500 font-body mt-1">Manage retail pricing rules for products</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handlePreviewPricing}
            className="border-ivory-300"
            data-testid="preview-pricing-btn"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview Changes
          </Button>
          <Button
            variant="outline"
            onClick={handleApplyPricing}
            className="border-gold-500 text-gold-600 hover:bg-gold-50"
            data-testid="apply-pricing-btn"
          >
            <Calculator className="w-4 h-4 mr-2" />
            Apply Rules
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-gold-500 hover:bg-gold-600 text-white"
            data-testid="add-rule-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Rule
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-800">How Pricing Rules Work</p>
              <ul className="text-sm text-blue-700 mt-1 space-y-1">
                <li>• <strong>Priority:</strong> Product-specific rules override category rules, which override site-wide rules</li>
                <li>• <strong>Higher priority number</strong> wins when multiple rules match at the same scope</li>
                <li>• <strong>Cost prices</strong> are stored internally; customers only see retail prices</li>
                <li>• <strong>4x markup = 300%</strong> (Cost + 300% of cost = Cost × 4)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Result */}
      {previewResult && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-amber-800">Preview Results</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setPreviewResult(null)}
              >
                Dismiss
              </Button>
            </div>
            <p className="text-amber-700">
              {previewResult.products_to_update} of {previewResult.products_checked} products would be updated
            </p>
            {previewResult.updates?.length > 0 && (
              <div className="mt-3 max-h-40 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-amber-600">
                      <th className="pb-2">Product</th>
                      <th className="pb-2">Cost</th>
                      <th className="pb-2">Old Retail</th>
                      <th className="pb-2">New Retail</th>
                    </tr>
                  </thead>
                  <tbody className="text-amber-800">
                    {previewResult.updates.slice(0, 10).map((u, i) => (
                      <tr key={i} className="border-t border-amber-200">
                        <td className="py-1">{u.name?.substring(0, 30)}...</td>
                        <td className="py-1">${u.cost?.toFixed(2)}</td>
                        <td className="py-1">${u.old_retail?.toFixed(2)}</td>
                        <td className="py-1 font-semibold">${u.new_retail?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewResult.updates.length > 10 && (
                  <p className="text-xs text-amber-600 mt-2">
                    ... and {previewResult.updates.length - 10} more
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Rules List */}
      <div className="grid gap-4">
        {rules.length === 0 ? (
          <Card className="bg-ivory-100 border-ivory-300">
            <CardContent className="p-8 text-center">
              <DollarSign className="w-12 h-12 mx-auto text-ivory-400 mb-4" />
              <h3 className="font-semibold text-ivory-700 mb-2">No Pricing Rules</h3>
              <p className="text-ivory-500 mb-4">Create your first pricing rule to calculate retail prices</p>
              <Button 
                onClick={() => { resetForm(); setShowModal(true); }}
                className="bg-gold-500 hover:bg-gold-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Rule
              </Button>
            </CardContent>
          </Card>
        ) : (
          rules.map((rule) => (
            <Card 
              key={rule.id} 
              className={`border ${rule.status === 'active' ? 'border-green-200 bg-green-50/30' : 'border-ivory-300 bg-ivory-50'}`}
              data-testid={`rule-card-${rule.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-ivory-800">{rule.name}</h3>
                      <Badge variant={rule.status === 'active' ? 'default' : 'secondary'}>
                        {rule.status}
                      </Badge>
                      <Badge variant="outline" className="bg-white">
                        Priority: {rule.priority}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-ivory-500">Type:</span>{' '}
                        <span className="text-ivory-700">{getRuleTypeLabel(rule.rule_type)}</span>
                      </div>
                      <div>
                        <span className="text-ivory-500">Calculation:</span>{' '}
                        <span className="text-ivory-700 font-medium">{getRuleDescription(rule)}</span>
                      </div>
                      <div>
                        <span className="text-ivory-500">Scope:</span>{' '}
                        <span className="text-ivory-700">{getScopeLabel(rule.scope)}</span>
                      </div>
                    </div>
                    
                    {rule.description && (
                      <p className="text-sm text-ivory-500 mt-2">{rule.description}</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleClone(rule)}
                      className="text-blue-500 hover:text-blue-600"
                      title="Clone this rule"
                      data-testid={`clone-rule-${rule.id}`}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(rule)}
                      data-testid={`edit-rule-${rule.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(rule.id)}
                      className="text-red-500 hover:text-red-600"
                      data-testid={`delete-rule-${rule.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Pricing Rule' : 'Create Pricing Rule'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Rule Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Awards 4x Markup"
                required
                data-testid="rule-name-input"
              />
            </div>

            <div>
              <Label>Description (Optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe what this rule does..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rule Type</Label>
                <Select
                  value={formData.rule_type}
                  onValueChange={(v) => setFormData({...formData, rule_type: v})}
                >
                  <SelectTrigger data-testid="rule-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="markup_percent">Percentage Markup</SelectItem>
                    <SelectItem value="markup_fixed">Fixed Amount Markup</SelectItem>
                    <SelectItem value="fixed_price">Fixed Price Override</SelectItem>
                    <SelectItem value="quantity_tier">Quantity Tiers</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Scope</Label>
                <Select
                  value={formData.scope}
                  onValueChange={(v) => setFormData({...formData, scope: v})}
                >
                  <SelectTrigger data-testid="scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="site_wide">All Products</SelectItem>
                    <SelectItem value="category">By Category</SelectItem>
                    <SelectItem value="product">Specific Products</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Rule-specific inputs */}
            {formData.rule_type === 'markup_percent' && (
              <div>
                <Label>Markup Percentage (e.g., 300 for 4x)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={formData.markup_percent}
                    onChange={(e) => setFormData({...formData, markup_percent: e.target.value})}
                    min="0"
                    step="1"
                    data-testid="markup-percent-input"
                  />
                  <span className="text-ivory-500">%</span>
                </div>
                <p className="text-xs text-ivory-500 mt-1">
                  Result: Cost × {(1 + parseFloat(formData.markup_percent || 0) / 100).toFixed(2)}
                </p>
              </div>
            )}

            {formData.rule_type === 'markup_fixed' && (
              <div>
                <Label>Fixed Markup Amount ($)</Label>
                <Input
                  type="number"
                  value={formData.markup_fixed}
                  onChange={(e) => setFormData({...formData, markup_fixed: e.target.value})}
                  min="0"
                  step="0.01"
                />
              </div>
            )}

            {formData.rule_type === 'fixed_price' && (
              <div>
                <Label>Fixed Retail Price ($)</Label>
                <Input
                  type="number"
                  value={formData.fixed_price}
                  onChange={(e) => setFormData({...formData, fixed_price: e.target.value})}
                  min="0"
                  step="0.01"
                />
              </div>
            )}

            {/* Quantity Tiers UI */}
            {formData.rule_type === 'quantity_tier' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label>Quantity Pricing Tiers</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addTier} data-testid="add-tier-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Tier
                  </Button>
                </div>
                
                {formData.quantity_tiers.length === 0 ? (
                  <p className="text-sm text-ivory-500 text-center py-4 bg-ivory-100 rounded">
                    No tiers defined. Click &quot;Add Tier&quot; to create quantity-based pricing.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-ivory-500 px-1">
                      <div className="col-span-2">Min Qty</div>
                      <div className="col-span-2">Max Qty</div>
                      <div className="col-span-3">Markup %</div>
                      <div className="col-span-3">+ Flat Fee</div>
                      <div className="col-span-2"></div>
                    </div>
                    {formData.quantity_tiers.map((tier, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-center" data-testid={`tier-row-${index}`}>
                        <Input
                          type="number"
                          className="col-span-2"
                          value={tier.min_qty}
                          onChange={(e) => updateTier(index, 'min_qty', e.target.value)}
                          min="1"
                          placeholder="1"
                        />
                        <Input
                          type="number"
                          className="col-span-2"
                          value={tier.max_qty || ''}
                          onChange={(e) => updateTier(index, 'max_qty', e.target.value || null)}
                          placeholder="∞"
                        />
                        <div className="col-span-3 flex items-center gap-1">
                          <Input
                            type="number"
                            value={tier.markup_percent}
                            onChange={(e) => updateTier(index, 'markup_percent', e.target.value)}
                            min="0"
                          />
                          <span className="text-ivory-400 text-sm">%</span>
                        </div>
                        <div className="col-span-3 flex items-center gap-1">
                          <span className="text-ivory-400 text-sm">$</span>
                          <Input
                            type="number"
                            value={tier.flat_addition || 0}
                            onChange={(e) => updateTier(index, 'flat_addition', e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="col-span-2 text-red-500 hover:text-red-600"
                          onClick={() => removeTier(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-ivory-500">
                  Leave &quot;Max Qty&quot; empty for unlimited. Flat fee is added per unit after markup calculation.
                </p>
              </div>
            )}

            {/* Category selection for category scope */}
            {formData.scope === 'category' && (
              <div>
                <Label>Category Codes (comma-separated)</Label>
                <Input
                  value={formData.category_ids.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData, 
                    category_ids: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  placeholder="e.g., CRY, GLS, ACR"
                />
                <p className="text-xs text-ivory-500 mt-1">
                  Available: CRY, GLS, ACR, PLA, TRO, VAS, BWL, CLK, etc.
                </p>
              </div>
            )}

            {/* Product selection for product scope */}
            {formData.scope === 'product' && (
              <div>
                <Label>Product SKUs (comma-separated)</Label>
                <Textarea
                  value={formData.product_ids.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData,
                    product_ids: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  placeholder="e.g., ECA-CRY-0001, ECA-GLS-0025"
                  rows={2}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority (higher = more important)</Label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 0})}
                  min="0"
                  data-testid="priority-input"
                />
              </div>

              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({...formData, status: v})}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-gold-500 hover:bg-gold-600 text-white" data-testid="save-rule-btn">
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPricing;
